import { DrawingLayer } from 'business/models/DrawingLayer';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  ExperimentDTO,
  ISOWellDropScoreDTO,
  LayerDetailsDTO,
  LayerDTO,
  LayerTypeDTO,
  SavePlateDTO,
} from 'business/models/Models';
import { generate } from 'business/plateviewer/Generate/GenerateCommon';
import React, { useContext, useEffect, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { isNullOrUndefined, showAlertSnackBar, showSnackBar } from 'utils/Global';
import useRockFetchGet, { useRockFetchPost } from 'utils/RockFetch';
import { OptimizationContext } from './OptimizationContext';
import { pasteNewLayerUtil, SubstituteWithPreparedStock } from '../PasteLayerHelper';
import WizardService from 'business/services/WizardService';
import { CopyWellDetail, ISOPreviousPlateData } from 'business/models/CustomModels';
import {
  createIterativeScreenOptimization,
  getPrecipetantIngredientRows,
} from './IterativeScreenOptimizationHelper';
import { ISOIngrdientInfo } from './ISOIngredientInfo';
import { useRMSelector } from 'redux/hooks';
import {
  createSaveExpDTO,
  generateEmptyCanvasItems,
  isPlateHaveLayer,
  removeExperimentFromScreen,
} from 'utils/ExperimentUtils';
import ExperimentService from 'business/services/ExperimentService';
import { TreeService } from 'business/services/TreeService';
import { CHANNEL_ACTIONS } from 'business/broadcast';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { GetPlateIngredientsService } from 'business/services/IngredientService';
import { Plate } from 'components/Experiment/constants';
import { useAllWellNavigationDetails } from 'redux/hooks/experiment';
import { showPopupBlockedModal } from 'components/reports/utils';
import { useModalHandlers } from 'components/modal';
import { createOptimizedGridLayers } from './utils';
import { GRID_OPTIMIZATION_WARNING_HEADER } from 'business/optimization/constants';

type CreateOptimizationProps = {
  wellDetails: CopyWellDetail[];
};

const POPUP_BLOCKED_MESSAGE =
  'Unable to open experiment automatically. Please make sure pop-ups and redirects are allowed then try again.';
const POPUP_BLOCKED_TITLE = 'Experiment Opening Failed';

export const CreateOptimization = ({ wellDetails }: CreateOptimizationProps) => {
  const treeService: TreeService = new TreeService();
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isNameDuplicateError, setIsNameDuplicateError] = useState<boolean>(false);
  const context = useContext(OptimizationContext);
  const [rockFetchGet] = useRockFetchGet();
  const [rockFetchPost] = useRockFetchPost();
  const wizardService: WizardService = new WizardService();
  const exp = useRMSelector((state) => state.experiment.expData);
  const selectedWells = useAllWellNavigationDetails();
  const { showModal, hideModal } = useModalHandlers();

  useEffect(() => {
    context.setNextEnabled(false);
    context.setBackDisabled(true);
    async function loadExpDetails() {
      let expData: ExperimentDtoClient;
      if (!context.isNewExperiment) {
        expData = await rockFetchGet(
          `api/experiment/exp/${context.experimentId}`,
          'get',
          undefined,
          true,
        );

        const tempLyrs = await rockFetchGet(
          `/api/Experiment/layers/${context.experimentId}`,
          'get',
          undefined,
          true,
        );

        const layers: DrawingLayer[] = !isNullOrUndefined(tempLyrs)
          ? tempLyrs.map((tl) => LayerDTO.fromJS(tl))
          : new Array<DrawingLayer>();
        if (isNullOrUndefined(layers)) {
          showSnackBar('Something Worng Happened');
          context.setShow(false);
        } else {
          expData.layers = layers;
        }

        const tempLyrDetails = await rockFetchGet(
          `/api/Experiment/layerdetails/${context.experimentId}`,
          'Get',
          undefined,
          true,
        );

        const layersDetails: LayerDetailsDTO[] = !isNullOrUndefined(tempLyrDetails)
          ? tempLyrDetails.map((tld) => LayerDetailsDTO.fromJS(tld))
          : new Array<LayerDetailsDTO>();
        if (!isNullOrUndefined(layersDetails)) {
          layersDetails.forEach((ld) => {
            const tempLyr = expData.layers.find((l) => l.id == ld.layerId);

            if (!isNullOrUndefined(tempLyr)) {
              tempLyr.layerDetail = ld;
            }
          });
        }
        await loadIngredients(expData.plateDto);
        generateEmptyCanvasItems(expData);
      } else {
        const uniquePlates = new Set(selectedWells.map((item) => item.plateID)).size;
        const body = {
          paretnTreeNodeId: context.experimentId,
          plateTypeId: context.plateTypeId,
          plateRows:
            context.layerType === LayerTypeDTO.Screen ? exp.rowCount : uniquePlates,
          plateCols: context.layerType === LayerTypeDTO.Screen ? exp.colCount : 1,
        };
        const response = await rockFetchPost(
          'api/experiment/createDefaultUnsavedExperiment',
          body,
        );
        if (response !== null) {
          expData = response;
        } else {
          showSnackBar('Something Wrong Happened');
          context.setShow(false);
        }
        expData.layers = [];
      }
      let expDto: any;

      if (context.layerType === LayerTypeDTO.Screen) {
        const ingredientInfoList = await getIngredientInfoListData();
        const optimizationResult = createIterativeScreenOptimization(
          { ...expData },
          exp,
          ingredientInfoList,
        );
        if (optimizationResult.error === true) {
          setIsError(true);
          context.setBackDisabled(false);
        } else {
          expDto = optimizationResult.expData;
        }
      } else {
        if (
          context.layerType === LayerTypeDTO.Grid &&
          context.optimizationConfig.gridWellsEnabled
        ) {
          const result = await createOptimizedGridLayers(
            exp,
            expData,
            selectedWells,
            context.optimizationConfig,
          );
          expDto = result.expData;
          if (result.errors.length > 0) {
            showModal({
              heading: 'Grid Layer',
              body: (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  <p>{GRID_OPTIMIZATION_WARNING_HEADER}</p>
                  {result.errors.map((error, index) => (
                    <p key={index}>{error}</p>
                  ))}
                </div>
              ),
              okButtonText: 'OK',
              isOkOnly: true,
              onOk: hideModal,
            });
          }
          if (isNullOrUndefined(expDto)) {
            return;
          }
          const generated = generate(expDto, false);
          if (isNullOrUndefined(generated)) {
            return;
          }
        } else {
          if (context.substituteStock) {
            SubstituteWithPreparedStock(wellDetails);
          }
          expDto = pasteNewLayerUtil(expData, context.layerType, [...wellDetails], 1);

          if (isNullOrUndefined(expDto)) {
            return;
          }
          const generated = generate(expDto, false);
          if (isNullOrUndefined(generated)) {
            return;
          }
        }
      }
      return expDto;
    }
    loadExpDetails().then(async (data: ExperimentDtoClient) => {
      if (isNullOrUndefined(data)) {
        setLoading(false);
        context.setNextEnabled(true);
        return;
      }
      if (context.isNewExperiment) {
        data.layers = removeExperimentFromScreen(data.layers);

        const expDto: ExperimentDTO = new ExperimentDTO();
        expDto.init(data);
        expDto.expName = context.experimentName;
        const parentTreeNodeId: number = context.experimentId;
        let isoScores: ISOWellDropScoreDTO[] = null;
        if (context.layerType === LayerTypeDTO.Screen) {
          const currPlate: Plate = exp.plateDto.find((p) => p.plateNumber === 1);
          for (const plt of expDto.plateDto) {
            if (plt.plateNumber === 1) {
              plt.isoPreviousPlateID = currPlate.plateId;
              plt.isoRoundNumber = isNullOrUndefined(currPlate.isoRoundNumber)
                ? 1
                : currPlate.isoRoundNumber + 1;
            }
          }
          isoScores = getISOWellDropScoresList();
        }

        const saveExpReq = wizardService.saveWizardExperiment(
          expDto,
          undefined,
          parentTreeNodeId,
          data.layers,
          isoScores,
          context.plateTypeId,
        );
        saveExpReq
          .then(async (response) => {
            if (response === null) {
              showSnackBar('Cannot Save Optimization!');
            } else if (context.openExperiment == true) {
              const windowResponse = window.open(
                `/exp/${response.value}`,
                `/exp/${response.value}`,
              );
              if (!windowResponse) {
                showPopupBlockedModal(
                  showModal,
                  null,
                  hideModal,
                  POPUP_BLOCKED_MESSAGE,
                  POPUP_BLOCKED_TITLE,
                );
              }
            }
            setLoading(false);
            context.setNextEnabled(true);

            const treeNode = await treeService.getTreeNodeByExpId(response.value);
            context.explorerBroadcastChannel?.postMessage({
              action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
              payload: treeNode,
            });
          })
          .catch((exception) => {
            showAlertSnackBar(EAlertType.ERROR, ESnackBarPosition.TOP, exception.message);
            setIsNameDuplicateError(true);
            setLoading(false);
          });
      } else {
        const plates: SavePlateDTO[] = [];
        for (const plate of data.plateDto) {
          const pl = {
            ingredients: plate.ingredients,
            rowPos: plate.rowPos,
            colPos: plate.colPos,
          };
          const plateHaveLayer = isPlateHaveLayer(plate, data.layers);
          if (plateHaveLayer) {
            plates.push(pl as SavePlateDTO);
          }
        }
        const saveExpDTO = createSaveExpDTO(data, plates, [], []);
        const expService = new ExperimentService();
        const response = await expService.saveExperiment(saveExpDTO);
        if (!response || !response?.experimentData) {
          showSnackBar('Cannot Save Optimization!');
        } else if (context.openExperiment == true) {
          const windowResponse = window.open(
            `/exp/${context.experimentId}`,
            `/exp/${context.experimentId}`,
          );
          if (!windowResponse) {
            showPopupBlockedModal(
              showModal,
              null,
              hideModal,
              POPUP_BLOCKED_MESSAGE,
              POPUP_BLOCKED_TITLE,
            );
          }
        }
        setLoading(false);
        context.setNextEnabled(true);
      }
    });
  }, []);

  async function getIngredientInfoListData(): Promise<ISOIngrdientInfo[]> {
    const prevPlateData = await getPreviousPlateData();
    const ingredientInfoList = await getPrecipetantIngredientRows(
      exp,
      context.isoScores,
      prevPlateData,
    );
    return ingredientInfoList;
  }

  async function getPreviousPlateData(): Promise<ISOPreviousPlateData> {
    const previousPlateData: ISOPreviousPlateData = new ISOPreviousPlateData();
    const previousPlateId: number = exp.plateDto.find(
      (p) => p.plateNumber === 1,
    ).isoPreviousPlateID;
    if (previousPlateId > 0) {
      const tempLyrs = await rockFetchGet(
        `/api/Experiment/layers/${context.experimentId}`,
        'get',
        undefined,
        true,
      );
      const tempIngredients = await rockFetchGet(
        `api/ingredient/plate/${previousPlateId}`,
      );
      const scores = await rockFetchGet(`api/plate/platescores/${previousPlateId}`);
      previousPlateData.ingredients = tempIngredients;
      previousPlateData.layers = tempLyrs;
      previousPlateData.scores = scores;
    }
    return previousPlateData;
  }

  function getISOWellDropScoresList(): ISOWellDropScoreDTO[] {
    const isoScoresList: ISOWellDropScoreDTO[] = [];

    for (let i = 0; i < context.isoScores.length; i++) {
      const newIsoScore: ISOWellDropScoreDTO = new ISOWellDropScoreDTO();
      newIsoScore.wellDropScoreId = context.isoScores[i].wellDropScoreId;
      newIsoScore.isovalue = context.isoScores[i].isoValue;

      isoScoresList.push(newIsoScore);
    }
    return isoScoresList;
  }

  async function loadIngredients(plates) {
    for (const plate of plates) {
      if (plate.plateId > 0) {
        const ings = await GetPlateIngredientsService(plate.plateId);
        if (ings === null) {
          return null;
        }
        plate.ingredients = ings;
      }
    }
  }

  return (
    <div id="platerviewer-exp-optimization-wrapper" style={{ textAlign: 'center' }}>
      {isLoading && (
        <div
          id="platerviewer-exp-optimization-container"
          className="flex flex-col items-center"
        >
          <Row id="platerviewer-exp-optimization-content-row">
            <Col id="platerviewer-exp-optimization-content-col">
              <Spinner
                id="platerviewer-exp-optimization-content-spinner"
                animation="border"
                style={{ color: '#F89723' }}
              />
            </Col>
          </Row>
          <Row>
            <Col id="platerviewer-exp-optimization-content-col-header">
              Creating Optimization
            </Col>
          </Row>
        </div>
      )}
      {!isLoading && !isError && !isNameDuplicateError && (
        <>
          <div
            id="platerviewer-exp-optimization-success-title"
            style={{ fontWeight: 700, fontSize: '18px' }}
          >
            Congratulations!
          </div>
          <br id="platerviewer-exp-optimization-success-br" />
          <p id="platerviewer-exp-optimization-success-text-1" className="m-0">
            The wizard has successfully optimized your experiment.
          </p>
          <p id="platerviewer-exp-optimization-success-text-2" className="m-0">
            Your experiment can be found in the following folder:
          </p>
          <p
            id="platerviewer-exp-optimization-success-url"
            className="m-0"
            style={{ fontWeight: 'bold' }}
          >
            {context.experimentUrl}
          </p>
        </>
      )}
      {!isLoading && isError && (
        <>
          <div
            id="platerviewer-exp-optimization-error-invalid-title"
            style={{ fontWeight: 700, fontSize: '18px' }}
          >
            Error!
          </div>
          <br id="platerviewer-exp-optimization-error-invalid-br" />
          <p id="platerviewer-exp-optimization-error-invalid-text" className="m-0">
            The wizard could not optimized your experiment because of invalid
            concentrations.
          </p>
        </>
      )}
      {!isLoading && isNameDuplicateError && (
        <>
          <div
            id="platerviewer-exp-optimization-error-duplicate-title"
            style={{ fontWeight: 700, fontSize: '18px' }}
          >
            Error!
          </div>
          <br id="platerviewer-exp-optimization-error-duplicate-br" />
          <p id="platerviewer-exp-optimization-error-duplicate-text" className="m-0">
            The wizard could not optimized your experiment because of the name already
            exists.
          </p>
        </>
      )}
    </div>
  );
};
